import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import axios from "axios";
import { Target, Trophy, ArrowLeft, RefreshCw, MapPin, Crosshair, Clock, Zap } from "lucide-react";
import BracketDiagram, { TournamentFlowStepper } from "@/components/BracketDiagram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicScoreboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const venueParam = searchParams.get("v");

  const [event, setEvent] = useState(null);
  const [currentRound, setCurrentRound] = useState(null);
  const [boardStatus, setBoardStatus] = useState(null);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tournamentState, setTournamentState] = useState(null);
  const [knockoutState, setKnockoutState] = useState(null);
  const [venues, setVenues] = useState([]);
  const [showPicker, setShowPicker] = useState(false);
  const [upcoming, setUpcoming] = useState(null);
  const [prevRound, setPrevRound] = useState(null);
  const [roundTransition, setRoundTransition] = useState(false);
  const roundRef = useRef(null);

  const loadEvent = useCallback(async (vid) => {
    try {
      const url = vid ? `${API}/events/active?venue=${vid}` : `${API}/events/active`;
      const eventRes = await axios.get(url);
      if (!eventRes.data) { setEvent(null); return; }
      setEvent(eventRes.data);
      const isContinuous = eventRes.data.pace_mode === "continuous";
      const fetches = [
        axios.get(`${API}/events/${eventRes.data.id}/matches/current-round`),
        axios.get(`${API}/events/${eventRes.data.id}/teams`),
        axios.get(`${API}/events/${eventRes.data.id}/matches/upcoming`),
      ];
      if (isContinuous) {
        fetches.push(axios.get(`${API}/events/${eventRes.data.id}/matches/board-status`));
      }
      const results = await Promise.all(fetches);
      const [roundRes, teamsRes, upcomingRes] = results;
      // Detect round change for animation
      const newRound = roundRes.data?.round_number;
      if (roundRef.current !== null && roundRef.current !== newRound) {
        setPrevRound(roundRef.current);
        setRoundTransition(true);
        setTimeout(() => setRoundTransition(false), 800);
      }
      roundRef.current = newRound;
      setCurrentRound(roundRes.data);
      setTeams(teamsRes.data.sort((a, b) => b.wins - a.wins || a.losses - b.losses));
      setUpcoming(upcomingRes.data);
      if (isContinuous && results[3]) {
        setBoardStatus(results[3].data);
      } else {
        setBoardStatus(null);
      }
      if (eventRes.data.event_type === "tournament") {
        try {
          const tsRes = await axios.get(`${API}/events/${eventRes.data.id}/tournament/state`);
          setTournamentState(tsRes.data);
        } catch (e) { /* no state yet */ }
      }
      if (eventRes.data.event_type === "knockout") {
        try {
          const koRes = await axios.get(`${API}/events/${eventRes.data.id}/knockout/state`);
          setKnockoutState(koRes.data);
        } catch (e) { setKnockoutState(null); }
      } else {
        setKnockoutState(null);
      }
    } catch (e) {
      setEvent(null);
    }
  }, []);

  const loadVenues = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/venues/active`);
      setVenues(res.data || []);
      return res.data || [];
    } catch (e) { return []; }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (venueParam) {
        await loadEvent(venueParam);
        setShowPicker(false);
      } else {
        const v = await loadVenues();
        if (v.length === 1) {
          await loadEvent(v[0].venue_id);
          setShowPicker(false);
        } else if (v.length > 1) {
          setShowPicker(true);
        }
      }
      setLoading(false);
    };
    init();
  }, [venueParam, loadEvent, loadVenues]);

  useEffect(() => {
    if (!event || showPicker) return;
    const vid = venueParam || event.venue_id;
    const interval = setInterval(() => loadEvent(vid), 5000);
    return () => clearInterval(interval);
  }, [event, showPicker, venueParam, loadEvent]);

  const selectVenue = (vid) => {
    setSearchParams({ v: vid });
    setShowPicker(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 text-[#E3292E] animate-spin" />
      </div>
    );
  }

  if (showPicker && venues.length > 1) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6">
        <Target className="w-12 h-12 text-[#E3292E] mb-6" />
        <h1 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tighter mb-2" data-testid="venue-picker-title">
          Live Scoreboards
        </h1>
        <p className="text-white/40 text-sm mb-10 font-body">Select a venue to view their active event</p>
        <div className="w-full max-w-lg space-y-3">
          {venues.map((v, i) => (
            <button
              key={v.venue_id}
              onClick={() => selectVenue(v.venue_id)}
              className="w-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] hover:border-[#E3292E]/40 transition-colors p-5 text-left group"
              data-testid={`venue-option-${i}`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin className="w-4 h-4 text-[#E3292E]" />
                    <span className="font-heading font-bold text-white text-lg">{v.venue_name}</span>
                  </div>
                  <p className="text-white/50 text-sm font-body ml-6">{v.event_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="rounded-none text-xs bg-white/10 text-white/60">{v.game_format}</Badge>
                  <Badge className="rounded-none text-xs bg-white/10 text-white/60 capitalize">{v.play_mode}</Badge>
                  <Crosshair className="w-4 h-4 text-white/20 group-hover:text-[#E3292E] transition-colors" />
                </div>
              </div>
            </button>
          ))}
        </div>
        <Button onClick={() => navigate("/")} variant="ghost" className="mt-10 text-white/30 hover:text-white hover:bg-white/5 rounded-none" data-testid="picker-back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Home
        </Button>
      </div>
    );
  }

  if (!event || event.status === "setup") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-6 text-center">
        <Target className="w-16 h-16 text-[#E3292E] mb-6" />
        <h1 className="font-display text-4xl text-white uppercase" data-testid="no-event-title">No Active Event</h1>
        <p className="text-white/50 mt-4 font-body">Check back when the tournament begins</p>
        <Button onClick={() => navigate("/")} variant="outline" className="mt-8 rounded-none border-white/20 text-white hover:bg-white/10" data-testid="back-btn">
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      </div>
    );
  }

  const boardCount = event.effective_boards || event.num_boards || 4;
  const gridCols = boardCount >= 4 ? "xl:grid-cols-4" : boardCount >= 3 ? "lg:grid-cols-3" : "";

  return (
    <div
      className="min-h-screen bg-[#0A0A0A] relative"
      style={{
        backgroundImage: "url(https://static.prod-images.emergentagent.com/jobs/f707a822-bbe1-4c69-96b7-9259b8de4d00/images/fa601607ac9b437b637c4c3e42bab1ac5c49ac757de3c54913977ac43ba0f5bf.png)",
        backgroundSize: "cover", backgroundAttachment: "fixed",
      }}
    >
      <div className="absolute inset-0 bg-[#0A0A0A]/90" />

      {/* Round transition overlay */}
      {roundTransition && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]/80 backdrop-blur-sm" data-testid="round-transition-overlay">
          <div className="text-center animate-round-pop">
            <Zap className="w-12 h-12 text-[#E3292E] mx-auto mb-4 animate-pulse" />
            <p className="font-display text-5xl sm:text-7xl text-white uppercase tracking-tighter">
              Round {currentRound?.round_number}
            </p>
            <p className="text-white/40 text-sm mt-2 font-body">Get ready!</p>
          </div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-white/10 pb-4 mb-8">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate("/")} variant="ghost" className="text-white/50 hover:text-white hover:bg-white/5 rounded-none p-2" data-testid="scoreboard-back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl text-white uppercase tracking-tighter" data-testid="event-title">
                {event.name}
              </h1>
              <p className="text-white/50 text-sm font-body">{event.date}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {venueParam && (
              <Button
                onClick={() => { setSearchParams({}); setShowPicker(true); setEvent(null); loadVenues(); }}
                variant="ghost" className="text-white/30 hover:text-white hover:bg-white/5 rounded-none text-xs"
                data-testid="change-venue-btn"
              >
                <MapPin className="mr-1 h-3 w-3" /> Change Venue
              </Button>
            )}
            <Badge className={`rounded-none text-xs tracking-[0.2em] uppercase font-bold ${event.status === "completed" ? "bg-white/20 text-white" : "bg-[#E3292E] text-white"}`} data-testid="event-status-badge">
              {event.status === "completed" ? "COMPLETED" : "LIVE"}
            </Badge>
          </div>
        </div>

        {/* Tournament Flow Stepper */}
        {event.event_type === "tournament" && (
          <TournamentFlowStepper event={event} tournamentPhase={tournamentState?.tournament_phase || "round_robin"} />
        )}

        {/* Tournament Bracket View */}
        {event.event_type === "tournament" && tournamentState && (tournamentState.tournament_phase === "divisions" || tournamentState.tournament_phase === "complete") && (
          <div className="mb-10 space-y-8">
            {["winners", "losers"].map((divKey) => {
              const div = tournamentState.divisions?.[divKey];
              if (!div) return null;
              const tm = tournamentState.team_map || {};
              const divColor = divKey === "winners" ? "#309F6A" : "#E3292E";
              const divMatches = (tournamentState.bracket_matches || []).filter((m) => m.phase === `${divKey}_bracket`);
              return (
                <div key={divKey}>
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold mb-4" style={{ color: divColor }}>{div.name}</h2>
                  <div className="border border-white/10 bg-[#141414]/50 p-4">
                    <BracketDiagram matches={divMatches} teamMap={tm} championId={div.champion_id} byeTeamId={div.bye_team_id} divisionName={divKey} color={divColor} isActive={false} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ===== KNOCKOUT BRACKET VIEW (Public Scoreboard) ===== */}
        {event.event_type === "knockout" && knockoutState && (
          <div className="mb-10 space-y-8 animate-card-in">
            {/* Placements - show when any are determined */}
            {knockoutState.knockout?.placements && Object.keys(knockoutState.knockout.placements).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="knockout-placements">
                {["1st", "2nd", "3rd", "4th"].map((place, idx) => {
                  const tid = knockoutState.knockout.placements[place];
                  const team = tid ? knockoutState.team_map?.[tid] : null;
                  const colors = { "1st": "#FFD700", "2nd": "#C0C0C0", "3rd": "#CD7F32", "4th": "#666" };
                  return (
                    <div key={place} className="border p-4 text-center animate-card-in" style={{ animationDelay: `${idx * 100}ms`, borderColor: `${colors[place]}40`, backgroundColor: `${colors[place]}08` }} data-testid={`placement-${place}`}>
                      <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: colors[place] }}>{place} Place</p>
                      {team ? (
                        <>
                          <p className="font-heading font-bold text-white">{team.team_name}</p>
                          <p className="text-white/40 text-[10px] mt-1">{team.players?.length ? team.players.join(", ") : (team.player2 ? `${team.player1} & ${team.player2}` : team.player1 || "")}</p>
                        </>
                      ) : (
                        <p className="text-white/20 text-sm">TBD</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Main Bracket */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#E3292E] animate-pulse" />
                <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]" data-testid="knockout-bracket-label">
                  {knockoutState.knockout?.status === "complete" ? "Final Bracket" : "Knockout Bracket"}
                </h2>
                {knockoutState.knockout?.status === "complete" && (
                  <Badge className="bg-[#309F6A] text-white rounded-none text-[9px]">COMPLETE</Badge>
                )}
              </div>
              <div className="border border-white/10 bg-[#141414]/50 p-4">
                <BracketDiagram
                  matches={(knockoutState.matches || []).filter(m => !m.is_third_place)}
                  teamMap={knockoutState.team_map || {}}
                  championId={knockoutState.knockout?.placements?.["1st"] || null}
                  byeTeamId={null}
                  divisionName="knockout"
                  color="#E3292E"
                  isActive={false}
                />
              </div>
            </div>

            {/* 3rd Place Match */}
            {(() => {
              const tpMatch = (knockoutState.matches || []).find(m => m.is_third_place);
              if (!tpMatch) return null;
              const tm = knockoutState.team_map || {};
              const t1 = tm[tpMatch.team1_id] || {};
              const t2 = tm[tpMatch.team2_id] || {};
              const t1Won = tpMatch.winner_team_id === tpMatch.team1_id;
              const t2Won = tpMatch.winner_team_id === tpMatch.team2_id;
              return (
                <div className="border border-[#CD7F32]/30 bg-[#CD7F32]/5 p-4" data-testid="third-place-match">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#CD7F32]">3rd Place Match</h2>
                    {(t1Won || t2Won) && <Badge className="bg-[#CD7F32]/20 text-[#CD7F32] rounded-none text-[9px]">COMPLETE</Badge>}
                  </div>
                  <div className="flex items-center gap-4">
                    <div className={`flex-1 p-3 border text-center ${t1Won ? "border-[#CD7F32] bg-[#CD7F32]/10" : "border-white/10"}`}>
                      <p className={`font-heading font-bold ${t1Won ? "text-[#CD7F32]" : "text-white"}`}>{t1.team_name || "TBD"}</p>
                      {t1Won && <Trophy className="w-4 h-4 text-[#CD7F32] mx-auto mt-1" />}
                    </div>
                    <span className="text-white/20 text-xs font-bold tracking-[0.3em]">VS</span>
                    <div className={`flex-1 p-3 border text-center ${t2Won ? "border-[#CD7F32] bg-[#CD7F32]/10" : "border-white/10"}`}>
                      <p className={`font-heading font-bold ${t2Won ? "text-[#CD7F32]" : "text-white"}`}>{t2.team_name || "TBD"}</p>
                      {t2Won && <Trophy className="w-4 h-4 text-[#CD7F32] mx-auto mt-1" />}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Current Round Boards — continuous uses board-status, round-based uses current-round (not for knockout) */}
        {event.event_type !== "knockout" && (() => {
          const isContinuous = event.pace_mode === "continuous";
          const activeBoardCount = isContinuous && boardStatus ? boardStatus.boards : boardCount;
          const displayMatches = isContinuous && boardStatus
            ? boardStatus.active_matches
            : (currentRound?.matches || []);
          if (displayMatches.length === 0 && !isContinuous) return null;
          // For continuous, always show boards even if empty (waiting state)
          if (!isContinuous && !currentRound?.matches?.length) return null;
          return (
            <div className="mb-10" data-testid="current-round-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-[#E3292E] animate-pulse" />
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E]" data-testid="current-round-label">
                    {isContinuous ? "Now Playing" : `Round ${currentRound.round_number}`}
                  </h2>
                </div>
                {isContinuous && boardStatus && (
                  <span className="text-white/30 text-xs font-body">
                    {boardStatus.completed_matches} of {boardStatus.total_matches} complete
                  </span>
                )}
                {currentRound?.round_robin_complete && (
                  <Badge className="bg-white/10 text-white/60 rounded-none text-xs">BONUS ROUND</Badge>
                )}
                {!isContinuous && event.total_rounds > 0 && (
                  <RoundProgress current={currentRound.round_number} total={event.total_rounds} />
                )}
              </div>

              <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${gridCols}`}>
                {Array.from({ length: activeBoardCount }, (_, i) => i + 1).map((boardNum, idx) => {
                  const match = displayMatches.find((m) => m.board_number === boardNum);
                  if (!match)
                    return (
                      <div key={boardNum} className="border border-white/5 bg-white/[0.02] p-6 animate-card-in" style={{ animationDelay: `${idx * 80}ms` }} data-testid={`board-${boardNum}-empty`}>
                        <p className="text-xs tracking-[0.2em] uppercase text-white/30 font-bold">Board {boardNum}</p>
                        <p className="text-white/20 text-sm mt-4 font-body">{isContinuous ? "Waiting..." : "No match"}</p>
                      </div>
                    );
                  return <MatchBoardCard key={boardNum} match={match} boardNum={boardNum} delay={idx * 80} />;
                })}
              </div>

              {isContinuous && boardStatus && boardStatus.completed_matches > 0 && boardStatus.total_matches > 0 && (
                <div className="mt-4 w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-[#309F6A] transition-all duration-700" style={{ width: `${(boardStatus.completed_matches / boardStatus.total_matches) * 100}%` }} />
                </div>
              )}
            </div>
          );
        })()}

        {/* Next Up + Queued Matches (not for knockout) */}
        {event.event_type !== "knockout" && upcoming && event.status === "active" && ((upcoming.next_up && upcoming.next_up.length > 0) || (upcoming.waiting && upcoming.waiting.length > 0)) && (
          <div className="mb-10 animate-card-in" style={{ animationDelay: "300ms" }} data-testid="upcoming-section">
            {/* NEXT UP */}
            {upcoming.next_up && upcoming.next_up.length > 0 && (
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-2 h-2 rounded-full bg-[#309F6A] animate-pulse" />
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#309F6A]" data-testid="next-up-label">
                    Next Up &middot; {upcoming.available_boards || 0} board{(upcoming.available_boards || 0) !== 1 ? "s" : ""} available
                  </h2>
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${gridCols}`}>
                  {upcoming.next_up.map((match, idx) => (
                    <div key={match.id} className="border border-[#309F6A]/30 bg-[#309F6A]/5 p-4 animate-card-in" style={{ animationDelay: `${400 + idx * 80}ms` }} data-testid={`next-up-match-${idx}`}>
                      <div className="mb-3 flex items-center justify-between">
                        <Badge className="rounded-none text-[9px] bg-[#309F6A]/20 text-[#309F6A] border border-[#309F6A]/30">NEXT UP</Badge>
                        <span className="text-[9px] text-[#309F6A]/60 font-heading">Board {idx + 1}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-right">
                          <p className="font-heading font-bold text-white text-sm">{match.team1_name}</p>
                          {match.team1_players && <p className="text-white/40 text-[10px] mt-0.5">{match.team1_players}</p>}
                        </div>
                        <span className="text-[#309F6A]/40 text-[10px] tracking-[0.3em] font-bold flex-shrink-0">VS</span>
                        <div className="flex-1 text-left">
                          <p className="font-heading font-bold text-white text-sm">{match.team2_name}</p>
                          {match.team2_players && <p className="text-white/40 text-[10px] mt-0.5">{match.team2_players}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* REMAINING QUEUE */}
            {upcoming.waiting && upcoming.waiting.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-3 h-3 text-white/30" />
                  <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-white/30" data-testid="upcoming-label">
                    Queue &middot; {upcoming.waiting.length} more
                  </h2>
                </div>
                <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${gridCols}`}>
                  {upcoming.waiting.slice(0, 8).map((match, idx) => (
                    <div key={match.id} className="border border-dashed border-white/8 bg-white/[0.015] p-4 animate-card-in" style={{ animationDelay: `${500 + idx * 60}ms` }} data-testid={`upcoming-match-${idx}`}>
                      <div className="mb-3">
                        <Badge className="rounded-none text-[9px] bg-white/5 text-white/20">QUEUED</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-right">
                          <p className="font-heading font-bold text-white/50 text-sm">{match.team1_name}</p>
                          {match.team1_players && <p className="text-white/20 text-[10px] mt-0.5">{match.team1_players}</p>}
                        </div>
                        <span className="text-white/10 text-[10px] tracking-[0.3em] font-bold flex-shrink-0">VS</span>
                        <div className="flex-1 text-left">
                          <p className="font-heading font-bold text-white/50 text-sm">{match.team2_name}</p>
                          {match.team2_players && <p className="text-white/20 text-[10px] mt-0.5">{match.team2_players}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Standings */}
        {teams.length > 0 && (
          <div className="animate-card-in" style={{ animationDelay: "200ms" }}>
            <h2 className="text-xs tracking-[0.2em] uppercase font-bold text-[#E3292E] mb-6">Standings</h2>
            <div className="border border-white/10 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">#</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase">Team</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase hidden sm:table-cell">Players</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">P</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">W</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">L</TableHead>
                    <TableHead className="text-white/50 text-xs tracking-[0.2em] uppercase text-center">%</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team, idx) => {
                    const pct = team.games_played > 0 ? Math.round((team.wins / team.games_played) * 100) : 0;
                    return (
                      <TableRow key={team.id} className={`border-b border-white/5 ${team.removed ? "opacity-50" : "hover:bg-white/5"}`} data-testid={`standings-row-${idx}`}>
                        <TableCell className="font-display text-2xl text-white/30">{idx + 1}</TableCell>
                        <TableCell>
                          <span className={`font-heading font-bold ${team.removed ? "text-white/40 line-through" : "text-white"}`}>{team.team_name}</span>
                          {team.removed && <span className="ml-2 text-[9px] text-[#E3292E]/60 uppercase tracking-wider">(Removed)</span>}
                        </TableCell>
                        <TableCell className="text-white/40 text-sm hidden sm:table-cell">
                          {team.players ? team.players.join(", ") : (team.player2 ? `${team.player1} & ${team.player2}` : team.player1)}
                        </TableCell>
                        <TableCell className="text-center text-white/60">{team.games_played}</TableCell>
                        <TableCell className="text-center text-[#309F6A] font-bold">{team.wins}</TableCell>
                        <TableCell className="text-center text-[#E3292E]">{team.losses}</TableCell>
                        <TableCell className="text-center text-white font-bold">{pct}%</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Sub-components --- */

function RoundProgress({ current, total }) {
  if (!total || total <= 1) return null;
  const pct = Math.min(100, Math.round((current / total) * 100));
  return (
    <div className="flex items-center gap-2 ml-auto" data-testid="round-progress">
      <div className="w-24 h-1 bg-white/10 overflow-hidden">
        <div
          className="h-full bg-[#E3292E] transition-all duration-700 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-white/30 text-[10px] font-body">{current}/{total}</span>
    </div>
  );
}

function MatchBoardCard({ match, boardNum, delay }) {
  const isComplete = match.status === "completed";
  const team1Won = match.winner_team_id === match.team1_id;
  const team2Won = match.winner_team_id === match.team2_id;

  return (
    <div
      className={`border p-6 transition-all duration-500 animate-card-in ${isComplete ? "border-[#309F6A]/30 bg-[#309F6A]/5" : "border-white/10 bg-white/[0.03]"}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`board-${boardNum}-card`}
    >
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs tracking-[0.2em] uppercase text-white/50 font-bold">Board {boardNum}</p>
        <Badge className={`rounded-none text-xs ${isComplete ? "bg-[#309F6A] text-white" : "bg-white/10 text-white/60"}`}>
          {isComplete ? "DONE" : "PLAYING"}
        </Badge>
      </div>

      <div className="space-y-3">
        <TeamBlock name={match.team1_name} players={match.team1_players} isWinner={team1Won} />
        <p className="text-center text-white/20 text-xs tracking-[0.3em] uppercase font-bold">VS</p>
        <TeamBlock name={match.team2_name} players={match.team2_players} isWinner={team2Won} />
      </div>

      {(match.high_score || match.low_score || match.high_checkout) && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-0.5">
          {match.high_score && <p className="text-[#309F6A] text-[10px] font-body">High: {match.high_score.player} ({match.high_score.score})</p>}
          {match.high_checkout && <p className="text-yellow-400 text-[10px] font-body">Checkout: {match.high_checkout.player} ({match.high_checkout.score})</p>}
          {match.low_score && <p className="text-[#E3292E] text-[10px] font-body">Low: {match.low_score.player} ({match.low_score.score})</p>}
        </div>
      )}
    </div>
  );
}

function TeamBlock({ name, players, isWinner }) {
  return (
    <div className={`p-3 border transition-all duration-300 ${isWinner ? "border-[#309F6A] bg-[#309F6A]/10" : "border-white/5"}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className={`font-heading font-bold text-lg ${isWinner ? "text-[#309F6A]" : "text-white"}`}>{name}</p>
          <p className="text-white/40 text-xs">{players}</p>
        </div>
        {isWinner && <Trophy className="w-5 h-5 text-[#309F6A] animate-trophy-in" />}
      </div>
    </div>
  );
}
